import { useRef, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/factorial-one-react'
import {
  BasicTextEditorHandle,
  RichTextDisplay,
  Spinner,
} from '@factorialco/factorial-one-react/dist/experimental'
// import { ArrowLeft } from '@factorialco/factorial-one-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { MeetingsMeetingGenerateChatDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

// import app from 'modules/core/store/models/app'
import { SendMessageInput } from 'modules/meetings/components/SendMessageInput'
// import { emptyParagraph } from 'modules/meetings/lib/constants/TipTapBlock'
// import { useMeetingNotes } from 'modules/meetings/lib/hooks/useMeetingNotes'
import * as styles from 'modules/meetings/lib/utils/hiddenScrollBar.vanilla.css'

interface AIPageChatProps {
  meetingId: number
  textEditorRef?: React.RefObject<BasicTextEditorHandle> | null | undefined
}

interface ChatMessage {
  message: string
  role: 'user' | 'assistant'
}

export const AIPageChat = ({ meetingId, textEditorRef: _ }: AIPageChatProps) => {
  const [isLoading, setIsLoading] = useState(false)
  // const { notes, setNotes } = useMeetingNotes()
  const messagesContainerRef = useRef<HTMLDivElement>(null)

  const storageKey = LOCAL_STORAGE_KEYS.meetingChatHistory(meetingId.toString())

  const [chatHistory, setChatHistory] = useLocalStorage<ChatMessage[]>(storageKey, [])

  const [generateMeetingsChat] = useMutation(MeetingsMeetingGenerateChatDocument, {
    extractorFn: (data) => data.meetingsMutations.generateMeetingsChat,
  })

  const handleSendMessage = async (message: string) => {
    if (isLoading) return

    setIsLoading(true)

    const newUserMessage: ChatMessage = { message, role: 'user' }
    const updatedHistory = [...(chatHistory || []), newUserMessage]

    setChatHistory(updatedHistory)

    const messageHistory = updatedHistory.map((item) => item.message)
    const roleHistory = updatedHistory.map((item) => item.role)

    const result = await generateMeetingsChat({
      variables: {
        meetingId,
        messageHistory,
        roleHistory,
      },
    })

    const handler = handleResult(result, () => null)

    let finalHistory = updatedHistory

    if (handler?.success) {
      const assistantMessage: ChatMessage = {
        message: handler?.data?.chat?.lastMessage || '',
        role: 'assistant',
      }
      finalHistory = [...updatedHistory, assistantMessage]
    } else {
      const errorMessage: ChatMessage = {
        message: i18n.t('meetings.meeting.chat.error_generating_chat'),
        role: 'assistant',
      }
      finalHistory = [...updatedHistory, errorMessage]
    }

    setChatHistory(finalHistory)
    setIsLoading(false)
  }

  // const handleAddChatToNote = () => {
  //   try {
  //     const title = i18n.t('meetings.meeting.chat.ai_chat_block_title')
  //     const messages =
  //       chatHistory?.map(({ role, message }) => ({
  //         role: role || 'assistant',
  //         message: message || '',
  //       })) || []
  //     textEditorRef?.current?.insertChat(title, messages)

  //     const chatBlock = {
  //       type: 'chat',
  //       attrs: {
  //         data: {
  //           title,
  //           messages,
  //         },
  //         config: null,
  //         isOpen: false,
  //       },
  //     }

  //     const oldNotes = JSON.parse(notes || '{}')

  //     const newNotes = {
  //       type: 'doc',
  //       content: [...(oldNotes?.content || []), chatBlock, emptyParagraph],
  //     }
  //     setNotes(JSON.stringify(newNotes))

  //     app.setFlash({
  //       type: 'correct',
  //       message: i18n.t('meetings.meeting.chat.ai_chat_block_success'),
  //     })
  //   } catch (error) {
  //     app.setFlash({
  //       type: 'wrong',
  //       message: i18n.t('common.error'),
  //     })
  //   }
  // }

  return (
    <Box flexDirection="column" height="full" position="relative" overflowY="auto">
      <Box
        padding="s24"
        ref={messagesContainerRef}
        flexGrow
        gap="s20"
        flexDirection="column"
        overflowY="auto"
        className={styles.hiddenScrollBar}
        style={{
          scrollBehavior: 'smooth',
        }}
      >
        {chatHistory?.map((chat, index) => (
          <Box key={index} alignItems={chat.role === 'user' ? 'flexEnd' : 'flexStart'}>
            <Box
              key={index}
              flexDirection="column"
              gap="s8"
              background={chat.role === 'user' ? 'grey200' : 'grey000White'}
              borderRadius={{
                all: 'abs012',
              }}
              paddingX="s12"
              paddingY="s8"
              border={{
                all: {
                  width: 's1',
                  style: 'solid',
                  color: 'grey300',
                },
              }}
              style={{
                maxWidth: '80%',
              }}
            >
              <RichTextDisplay content={chat.message} />
            </Box>
          </Box>
        ))}
        {isLoading && (
          <Box padding="s12" alignItems="center" gap="s8" flexDirection="row">
            <Spinner size="small" />
            <Text size="200" weight="normal">
              {i18n.t('meetings.meeting.chat.loading')}
            </Text>
          </Box>
        )}
      </Box>

      <Box
        flexNoShrink
        position="relative"
        paddingX="s20"
        paddingBottom="s20"
        paddingTop="s8"
        gap="s8"
      >
        {chatHistory && chatHistory.length > 0 && (
          <Box flexDirection="row" gap="s8">
            {/* <Button
              label={i18n.t('meetings.meeting.chat.add_chat_to_note')}
              onClick={() => handleAddChatToNote()}
              icon={ArrowLeft}
              size="sm"
              variant="outline"
            /> */}
            <Button
              label={i18n.t('meetings.meeting.chat.clear_chat')}
              onClick={() => setChatHistory([])}
              size="sm"
              variant="outline"
            />
          </Box>
        )}
        <SendMessageInput
          disabled={isLoading}
          placeholder={i18n.t('meetings.meeting.chat.write_message_placeholder')}
          onSend={handleSendMessage}
          isSending={isLoading}
          handleClear={() => setChatHistory([])}
        />
      </Box>
    </Box>
  )
}
